import React, { useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";

export default function Register() {
  const API_BASE = import.meta.env.VITE_API_BASE_URL;
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Register user
      const res = await axios.post(`${API_BASE}/api/users/register`, form);
      const { token, user } = res.data;

      // Save login state locally
      localStorage.setItem("userToken", token);
      localStorage.setItem("userName", user.name);
      localStorage.setItem("userEmail", user.email);

      // Load client + server cart and merge
      try {
        const localCartRaw = localStorage.getItem("relaxb_cart");
        const localCart = localCartRaw ? JSON.parse(localCartRaw) : [];

        const serverRes = await axios.get(`${API_BASE}/api/users/cart`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const serverCart = serverRes.data.cart || [];

        const map = new Map();
        const add = (item) => {
          const key = item.key;
          const existing = map.get(key);

          if (existing) {
            map.set(key, {
              ...existing,
              qty: (existing.qty || 0) + (item.qty || 0),
            });
          } else {
            map.set(key, { ...item });
          }
        };

        localCart.forEach(add);
        serverCart.forEach(add);

        const merged = Array.from(map.values());

        // Save merged cart to server
        await axios.post(
          `${API_BASE}/api/users/cart`,
          { cart: merged },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Save merged cart locally
        localStorage.setItem("relaxb_cart", JSON.stringify(merged));
      } catch (mergeErr) {
        console.error("Cart merge on register error:", mergeErr);
      }

      // Redirect to My Orders
      navigate("/my-orders");
    } catch (err) {
      console.error("Register error:", err);
      setError(
        err.response?.data?.error ||
          "Something went wrong while creating your account."
      );
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center px-4 pt-20">
      <div className="w-full max-w-sm bg-white/90 p-8 rounded-2xl shadow-md border border-gray-100">
        <h1 className="text-2xl font-semibold text-center mb-2">
          Create your RelaxB account
        </h1>
        <p className="text-xs text-gray-500 text-center mb-6">
          Save your details, view past orders and make checkout faster.
        </p>

        {/* Verification Notice */}
        <p className="text-[11px] text-center text-amber-600 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg mb-4">
          A verification email will be sent. Please verify to access all features.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="text-xs text-gray-700">Name</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-black focus:border-black"
              placeholder="Full name"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="text-xs text-gray-700">Email</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-black focus:border-black"
              placeholder="you@example.com"
              required
            />
          </div>

          {/* Password */}
          <div>
            <label className="text-xs text-gray-700">Password</label>
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-black focus:border-black"
              placeholder="At least 6 characters"
              required
            />
          </div>

          {/* Error */}
          {error && (
            <div className="text-red-600 text-xs bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2.5 text-white rounded-full font-medium transition ${
              loading ? "bg-gray-400 cursor-not-allowed" : "bg-black hover:bg-gray-900"
            }`}
          >
            {loading ? "Creating account..." : "Create account"}
          </button>
        </form>

        {/* Login link */}
        <p className="mt-4 text-xs text-center text-gray-500">
          Already have an account?{" "}
          <Link to="/login" className="text-blue-600 hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
